import { ReactElement, useState, useEffect } from 'react';
import { HiChevronDown, HiChevronRight, HiXMark, HiArrowLeft, HiArrowRight, HiCheck } from 'react-icons/hi2';
import { useEquipmentCatalog } from '../../../../hooks/useEquipment';
import { EquipmentType, EquipmentCatalog as EquipmentCatalogType, Manufacturer } from '../../../../types/Equipment';
import { EQUIPMENT_ITEMS, EQUIPMENT_ITEMS_BY_DISPLAY } from '../../../../utils/equipmentNames';
import Spinner from '../../../../components/Spinner';

interface EquipmentCatalogProps {
  className?: string;
  session: any;
  equipmentParameters?: string[];
  setEquipmentParameters?: (params: string[]) => void;
  equipmentSelected?: any;
  setEquipmentSelected?: (selected: any) => void;
}

const EquipmentCatalog = ({
  className = '',
  session,
  setEquipmentParameters: propSetEquipmentParameters,
  setEquipmentSelected: propSetEquipmentSelected,
}: EquipmentCatalogProps): ReactElement => {
  const [isParametersExpanded, setIsParametersExpanded] = useState(false);
  const [isCatalogExpanded, setIsCatalogExpanded] = useState(false);
  const [isSelectedExpanded, setIsSelectedExpanded] = useState(false);
  const [selectedEquipmentType, setSelectedEquipmentType] = useState<EquipmentType>('frames');
  const [selectedManufacturerId, setSelectedManufacturerId] = useState<string | null>(null);
  const [selectedEquipment, setSelectedEquipment] = useState<{
    frames: any[];
    tyres: any[];
    wheels_front: any[];
    wheels_rear: any[];
  }>({
    frames: [],
    tyres: [],
    wheels_front: [],
    wheels_rear: [],
  });

  // Helper function to convert database parameter names to display names
  const convertDbParametersToDisplay = (dbParameters: string[]): string[] => {
    if (!dbParameters || !Array.isArray(dbParameters)) return [];

    return dbParameters
      .map((param) => {
        const equipmentProperty = EQUIPMENT_ITEMS[param];
        return equipmentProperty ? equipmentProperty.displayValue : null;
      })
      .filter((param): param is string => param !== null);
  };

  // Parameters state - initialize from session
  const [selectedParameters, setSelectedParameters] = useState<string[]>(() => {
    return convertDbParametersToDisplay(session?.equipment_parameters || []);
  });

  const availableParameters = Object.keys(EQUIPMENT_ITEMS_BY_DISPLAY);

  const { data: catalogData, isLoading, error } = useEquipmentCatalog();

  // Helper function to convert equipment UUIDs to full objects
  const convertUUIDsToEquipment = (equipmentUUIDs: any, catalog: any) => {
    if (!catalog || !equipmentUUIDs)
      return {
        frames: [],
        tyres: [],
        wheels_front: [],
        wheels_rear: [],
      };

    const result: any = {
      frames: [],
      tyres: [],
      wheels_front: [],
      wheels_rear: [],
    };

    // Convert each equipment type
    if (equipmentUUIDs.frames && catalog.frames) {
      result.frames = equipmentUUIDs.frames
        .map((id: string) => catalog.frames.find((item: any) => item.id === id))
        .filter(Boolean);
    }

    if (equipmentUUIDs.tyres && catalog.tyres) {
      result.tyres = equipmentUUIDs.tyres
        .map((id: string) => catalog.tyres.find((item: any) => item.id === id))
        .filter(Boolean);
    }

    if (equipmentUUIDs.wheels_front && catalog.wheels_front) {
      result.wheels_front = equipmentUUIDs.wheels_front
        .map((id: string) => catalog.wheels_front.find((item: any) => item.id === id))
        .filter(Boolean);
    }

    if (equipmentUUIDs.wheels_rear && catalog.wheels_rear) {
      result.wheels_rear = equipmentUUIDs.wheels_rear
        .map((id: string) => catalog.wheels_rear.find((item: any) => item.id === id))
        .filter(Boolean);
    }

    return result;
  };

  // Helper function to convert equipment objects back to UUIDs for database storage
  const convertEquipmentToUUIDs = (equipment: any) => {
    const result: any = {};

    Object.keys(equipment).forEach((type) => {
      if (equipment[type] && Array.isArray(equipment[type])) {
        result[type] = equipment[type].map((item: any) => item.id);
      }
    });

    return result;
  };

  // Load selected equipment from session when catalog is available
  useEffect(() => {
    if (catalogData && session?.equipment_selected) {
      const fullEquipment = convertUUIDsToEquipment(session.equipment_selected, catalogData);
      setSelectedEquipment(fullEquipment);
    }
  }, [catalogData, session?.equipment_selected]);

  // Extract catalog data from the response
  const catalog: EquipmentCatalogType | null = catalogData || null;

  // Get unique manufacturers for the selected equipment type
  const getManufacturersForType = (type: EquipmentType): Manufacturer[] => {
    if (!catalog) return [];

    const items = catalog[type] || [];
    const manufacturerMap = new Map<string, Manufacturer>();

    items.forEach((item: any) => {
      if (item.manufacturer_id && item.manufacturer) {
        manufacturerMap.set(item.manufacturer_id, {
          id: item.manufacturer_id,
          name: item.manufacturer.name,
        });
      }
    });

    return Array.from(manufacturerMap.values()).sort((a, b) => a.name.localeCompare(b.name));
  };

  // Get equipment items for selected type and manufacturer
  const getEquipmentItems = () => {
    if (!catalog || !selectedManufacturerId) return [];

    const items = catalog[selectedEquipmentType] || [];
    return items
      .filter((item: any) => item.manufacturer_id === selectedManufacturerId)
      .sort((a: any, b: any) => a.name.localeCompare(b.name));
  };

  const equipmentTypeLabels = {
    frames: 'Frame',
    tyres: 'Tyre',
    wheels_front: 'Wheel Front',
    wheels_rear: 'Wheel Rear',
  };

  // Helper functions for equipment selection
  const selectEquipment = (item: any, type: EquipmentType) => {
    const newSelectedEquipment = {
      ...selectedEquipment,
      [type]: [...selectedEquipment[type], item],
    };
    setSelectedEquipment(newSelectedEquipment);

    // Update parent with UUID format
    if (propSetEquipmentSelected) {
      const uuidFormat = convertEquipmentToUUIDs(newSelectedEquipment);
      propSetEquipmentSelected(uuidFormat);
    }
  };

  const deselectEquipment = (itemId: string, type: EquipmentType) => {
    const newSelectedEquipment = {
      ...selectedEquipment,
      [type]: selectedEquipment[type].filter((item) => item.id !== itemId),
    };
    setSelectedEquipment(newSelectedEquipment);

    // Update parent with UUID format
    if (propSetEquipmentSelected) {
      const uuidFormat = convertEquipmentToUUIDs(newSelectedEquipment);
      propSetEquipmentSelected(uuidFormat);
    }
  };

  const isEquipmentSelected = (itemId: string, type: EquipmentType) => {
    return selectedEquipment[type].some((item) => item.id === itemId);
  };

  // Parameter management functions
  const toggleParameter = (parameter: string) => {
    const newParameters = selectedParameters.includes(parameter)
      ? selectedParameters.filter((p) => p !== parameter)
      : [...selectedParameters, parameter];

    setSelectedParameters(newParameters);

    // Convert display parameters back to database format and update parent
    if (propSetEquipmentParameters) {
      const dbParameters = newParameters.map((displayParam) => {
        // Find the equipment property by display value
        const equipmentProperty = EQUIPMENT_ITEMS_BY_DISPLAY[displayParam];
        return equipmentProperty ? equipmentProperty.name : displayParam;
      });
      propSetEquipmentParameters(dbParameters);
    }
  };

  const moveParameterLeft = (index: number) => {
    if (index > 0) {
      const newParams = [...selectedParameters];
      [newParams[index - 1], newParams[index]] = [newParams[index], newParams[index - 1]];
      setSelectedParameters(newParams);

      // Update parent with database format
      if (propSetEquipmentParameters) {
        const dbParameters = newParams.map((displayParam) => {
          const equipmentProperty = EQUIPMENT_ITEMS_BY_DISPLAY[displayParam];
          return equipmentProperty ? equipmentProperty.name : displayParam;
        });
        propSetEquipmentParameters(dbParameters);
      }
    }
  };

  const moveParameterRight = (index: number) => {
    if (index < selectedParameters.length - 1) {
      const newParams = [...selectedParameters];
      [newParams[index], newParams[index + 1]] = [newParams[index + 1], newParams[index]];
      setSelectedParameters(newParams);

      // Update parent with database format
      if (propSetEquipmentParameters) {
        const dbParameters = newParams.map((displayParam) => {
          const equipmentProperty = EQUIPMENT_ITEMS_BY_DISPLAY[displayParam];
          return equipmentProperty ? equipmentProperty.name : displayParam;
        });
        propSetEquipmentParameters(dbParameters);
      }
    }
  };

  // Helper function to determine which equipment types should be visible based on selected parameters
  const getVisibleEquipmentTypes = (): EquipmentType[] => {
    const visibleTypes: EquipmentType[] = [];

    // Show frames if "Frames" is selected in parameters
    if (selectedParameters.includes('Frame')) {
      visibleTypes.push('frames');
    }

    // Show tyres if any tyre-related parameter is selected
    const tyreParams = ['Tyres', 'Tyre Front', 'Tyre Rear'];
    if (tyreParams.some((param) => selectedParameters.includes(param))) {
      visibleTypes.push('tyres');
    }

    // Show wheels front if "Wheels Front" is selected
    if (selectedParameters.includes('Wheel Front')) {
      visibleTypes.push('wheels_front');
    }

    // Show wheels rear if "Wheels Rear" is selected
    if (selectedParameters.includes('Wheel Rear')) {
      visibleTypes.push('wheels_rear');
    }

    return visibleTypes;
  };

  const visibleEquipmentTypes = getVisibleEquipmentTypes();

  // Auto-update selectedEquipmentType if current selection is not visible
  useEffect(() => {
    if (visibleEquipmentTypes.length > 0 && !visibleEquipmentTypes.includes(selectedEquipmentType)) {
      setSelectedEquipmentType(visibleEquipmentTypes[0]);
      setSelectedManufacturerId(null);
      // setSelectedModelId(null);
    }
  }, [visibleEquipmentTypes, selectedEquipmentType]);

  const manufacturers = getManufacturersForType(selectedEquipmentType);
  const equipmentItems = getEquipmentItems();

  return (
    <div className={`cr-rounded-lg cr-border cr-border-gray-200 cr-bg-white ${className}`}>
      {/* Equipment Category Label */}
      <div className="cr-rounded-t-lg cr-border-b cr-border-gray-200 cr-bg-gray-50 cr-px-4 cr-py-3">
        <h3 className="cr-text-lg cr-font-semibold cr-uppercase cr-text-cirrusBlue-400">EQUIPMENT</h3>
        <p className="cr-mt-1 cr-text-sm cr-font-medium cr-text-cirrusBlue-400">
          Select equipment to use in the session
        </p>
      </div>

      {/* Parameters Section */}
      <button
        type="button"
        onClick={() => setIsParametersExpanded(!isParametersExpanded)}
        className="cr-flex cr-w-full cr-items-center cr-gap-2 cr-p-4 cr-text-left cr-text-base cr-font-medium cr-text-cirrusBlue-400 hover:cr-bg-gray-50"
      >
        {isParametersExpanded ? (
          <HiChevronDown className="cr-size-5 cr-text-gray-500" />
        ) : (
          <HiChevronRight className="cr-size-5 cr-text-gray-500" />
        )}
        Parameters
      </button>

      {/* Parameters Expandable content */}
      {isParametersExpanded && (
        <div className="cr-px-4 cr-pb-4">
          {/* Parameter Buttons */}
          <div className="cr-mb-4 cr-flex cr-flex-wrap cr-gap-2">
            {availableParameters.map((parameter) => (
              <button
                key={parameter}
                type="button"
                onClick={() => toggleParameter(parameter)}
                className={`cr-rounded cr-px-4 cr-py-2 cr-text-sm cr-font-medium cr-transition-colors ${
                  selectedParameters.includes(parameter)
                    ? 'cr-bg-blue-600 cr-text-white'
                    : 'cr-bg-gray-200 cr-text-gray-700 hover:cr-bg-gray-300'
                }`}
              >
                {parameter}
              </button>
            ))}
          </div>

          {/* Selected Parameters Row with Controls */}
          {selectedParameters.length > 0 && (
            <div className="cr-rounded cr-border cr-border-gray-300 cr-bg-gray-50 cr-p-4">
              <h4 className="cr-mb-3 cr-font-medium cr-text-gray-900">Selected Parameters (in order):</h4>
              <div className="cr-flex cr-flex-wrap cr-gap-2">
                {selectedParameters.map((parameter, index) => (
                  <div
                    key={parameter}
                    className="cr-flex cr-items-center cr-gap-1 cr-rounded cr-border cr-border-gray-200 cr-bg-white cr-px-3 cr-py-2"
                  >
                    <span className="cr-text-sm cr-font-medium">{parameter}</span>
                    <div className="cr-ml-2 cr-flex cr-gap-1">
                      <button
                        type="button"
                        onClick={() => moveParameterLeft(index)}
                        disabled={index === 0}
                        className={`cr-rounded cr-p-1 cr-transition-colors ${
                          index === 0
                            ? 'cr-cursor-not-allowed cr-text-gray-300'
                            : 'cr-text-gray-600 hover:cr-bg-gray-100'
                        }`}
                        title="Move left"
                      >
                        <HiArrowLeft className="cr-size-3" />
                      </button>
                      <button
                        type="button"
                        onClick={() => moveParameterRight(index)}
                        disabled={index === selectedParameters.length - 1}
                        className={`cr-rounded cr-p-1 cr-transition-colors ${
                          index === selectedParameters.length - 1
                            ? 'cr-cursor-not-allowed cr-text-gray-300'
                            : 'cr-text-gray-600 hover:cr-bg-gray-100'
                        }`}
                        title="Move right"
                      >
                        <HiArrowRight className="cr-size-3" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Catalog Section */}
      <div className="cr-border-t cr-border-gray-200">
        <button
          type="button"
          onClick={() => setIsCatalogExpanded(!isCatalogExpanded)}
          className="cr-flex cr-w-full cr-items-center cr-gap-2 cr-p-4 cr-text-left cr-text-base cr-font-medium cr-text-cirrusBlue-400 hover:cr-bg-gray-50"
        >
          {isCatalogExpanded ? (
            <HiChevronDown className="cr-size-5 cr-text-gray-500" />
          ) : (
            <HiChevronRight className="cr-size-5 cr-text-gray-500" />
          )}
          Catalog
        </button>

        {/* Catalog Expandable content */}
        {isCatalogExpanded && (
          <div className="cr-px-4 cr-pb-4">
            {isLoading && (
              <div className="cr-flex cr-justify-center cr-py-8">
                <Spinner isFetching={true} />
              </div>
            )}

            {error && <div className="cr-py-4 cr-text-center cr-text-red-600">Error loading equipment catalog</div>}

            {catalog && visibleEquipmentTypes.length > 0 && (
              <>
                {/* Equipment Type Radio Buttons - Only show types selected in parameters */}
                <div className="cr-mb-6 cr-flex cr-flex-wrap cr-gap-4">
                  {visibleEquipmentTypes.map((type) => (
                    <label key={type} className="cr-flex cr-cursor-pointer cr-items-center cr-gap-2">
                      <input
                        type="radio"
                        name="equipmentType"
                        value={type}
                        checked={selectedEquipmentType === type}
                        onChange={(e) => {
                          setSelectedEquipmentType(e.target.value as EquipmentType);
                          setSelectedManufacturerId(null); // Reset manufacturer selection
                          // setSelectedModelId(null); // Reset model selection
                        }}
                        className="cr-h-4 cr-w-4 cr-text-blue-600"
                      />
                      <span className="cr-text-sm cr-font-medium cr-text-gray-700">{equipmentTypeLabels[type]}</span>
                    </label>
                  ))}
                </div>

                {/* Two-column layout */}
                <div className="cr-grid cr-grid-cols-2 cr-gap-4">
                  {/* Manufacturers Column */}
                  <div>
                    <h4 className="cr-mb-3 cr-font-medium cr-text-gray-900">Manufacturers</h4>
                    <div className="cr-h-64 cr-overflow-y-auto cr-rounded cr-border cr-border-gray-300 cr-bg-gray-50">
                      {manufacturers.length > 0 ? (
                        <div className="cr-p-2">
                          {manufacturers.map((manufacturer) => (
                            <button
                              key={manufacturer.id}
                              type="button"
                              onClick={() => {
                                setSelectedManufacturerId(manufacturer.id);
                                // setSelectedModelId(null); // Reset model selection
                              }}
                              className={`cr-mb-1 cr-w-full cr-rounded cr-p-2 
                            cr-text-left cr-text-sm cr-transition-colors ${
                              selectedManufacturerId === manufacturer.id
                                ? 'cr-bg-blue-100 cr-font-medium cr-text-blue-800'
                                : 'cr-text-gray-700 hover:cr-bg-gray-100'
                            }`}
                            >
                              {manufacturer.name}
                            </button>
                          ))}
                        </div>
                      ) : (
                        <div className="cr-flex cr-h-full cr-items-center cr-justify-center cr-text-sm cr-text-gray-500">
                          No manufacturers available
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Models Column */}
                  <div>
                    <h4 className="cr-mb-3 cr-font-medium cr-text-gray-900">Models</h4>
                    <div className="cr-h-64 cr-overflow-y-auto cr-rounded cr-border cr-border-gray-300 cr-bg-gray-50">
                      {selectedManufacturerId ? (
                        equipmentItems.length > 0 ? (
                          <div className="cr-p-2">
                            {equipmentItems.map((item: any) => {
                              const isSelected = isEquipmentSelected(item.id, selectedEquipmentType);
                              return (
                                <button
                                  key={item.id}
                                  type="button"
                                  onClick={() => {
                                    if (isSelected) {
                                      // If already selected, remove it
                                      deselectEquipment(item.id, selectedEquipmentType);
                                    } else {
                                      // If not selected, add it
                                      selectEquipment(item, selectedEquipmentType);
                                    }
                                  }}
                                  className={
                                    'cr-w-full cr-border-b cr-border-gray-100 cr-p-2 cr-text-left cr-text-sm' +
                                    ' cr-text-gray-700 cr-transition-colors last:cr-border-b-0 hover:cr-bg-gray-100'
                                  }
                                >
                                  <div className="cr-flex cr-items-center cr-justify-between">
                                    <span>{item.name}</span>
                                    {isSelected && <HiCheck className="cr-size-4 cr-flex-shrink-0 cr-text-green-600" />}
                                  </div>
                                </button>
                              );
                            })}
                          </div>
                        ) : (
                          <div className="cr-flex cr-h-full cr-items-center cr-justify-center cr-text-sm cr-text-gray-500">
                            No models available
                          </div>
                        )
                      ) : (
                        <div className="cr-flex cr-h-full cr-items-center cr-justify-center cr-text-sm cr-text-gray-500">
                          Select a manufacturer
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </>
            )}

            {catalog && visibleEquipmentTypes.length === 0 && (
              <div className="cr-py-8 cr-text-center cr-text-gray-500">
                <p>Please select equipment parameters above to show catalog options.</p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Selected Section */}
      <div className="cr-border-t cr-border-gray-200">
        <button
          type="button"
          onClick={() => setIsSelectedExpanded(!isSelectedExpanded)}
          className="cr-flex cr-w-full cr-items-center cr-gap-2 cr-p-4 cr-text-left cr-text-base cr-font-medium cr-text-cirrusBlue-400 hover:cr-bg-gray-50"
        >
          {isSelectedExpanded ? (
            <HiChevronDown className="cr-size-5 cr-text-gray-500" />
          ) : (
            <HiChevronRight className="cr-size-5 cr-text-gray-500" />
          )}
          Items Selected
        </button>

        {/* Selected Equipment Expandable content */}
        {isSelectedExpanded && (
          <div className="cr-px-4 cr-pb-4">
            <div className="cr-flex cr-gap-4">
              {(Object.keys(equipmentTypeLabels) as EquipmentType[]).map((type) => (
                <div key={type} className="cr-flex-1">
                  <h4 className="cr-mb-3 cr-text-center cr-font-medium cr-text-gray-900">
                    {equipmentTypeLabels[type]}
                  </h4>
                  <div className="cr-h-64 cr-overflow-y-auto cr-rounded cr-border cr-border-gray-300 cr-bg-gray-50">
                    {selectedEquipment[type].length > 0 ? (
                      <div className="cr-space-y-2 cr-p-2">
                        {selectedEquipment[type].map((item: any) => (
                          <div
                            key={item.id}
                            className="cr-rounded cr-border cr-border-gray-200 cr-bg-white cr-p-2 cr-text-sm"
                          >
                            <div className="cr-flex cr-items-start cr-justify-between cr-gap-2">
                              <div className="cr-min-w-0 cr-flex-1">
                                <div className="cr-truncate cr-font-medium cr-text-gray-900">{item.name}</div>
                                <div className="cr-truncate cr-text-xs cr-text-gray-500">{item.manufacturer?.name}</div>
                              </div>
                              <button
                                type="button"
                                onClick={() => deselectEquipment(item.id, type)}
                                className="cr-flex-shrink-0 cr-rounded cr-p-1 cr-text-red-600 cr-transition-colors hover:cr-bg-red-100"
                                title="Remove"
                              >
                                <HiXMark className="cr-size-4" />
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="cr-flex cr-h-full cr-items-center cr-justify-center cr-p-4 cr-text-center cr-text-sm cr-text-gray-500">
                        No {equipmentTypeLabels[type].toLowerCase()} selected
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default EquipmentCatalog;
